import { useCallback, useEffect, useMemo, useState } from "react";
import { FolderKanban, LogOut, MoreVertical, Search, Users } from "lucide-react";
import {
  deleteAdminWebBuilderLevel,
  getAdminWebBuilderLevel,
  listAdminWebBuilderLevels,
  listWebBuilderUsers,
  moderateAdminWebBuilderLevel
} from "./webBuilderApi";
import { buildUnityAssetText } from "./unityAssetExport";

const ROUTE_MANAGE_USERS = "/manage";
const ROUTE_MANAGE_LEVELS = "/manager/levels";

const inputClass =
  "w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm !text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200";

const btnPrimary =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-lime-500 !bg-lime-400 px-3 py-2 text-sm font-semibold !text-slate-900 transition hover:!bg-lime-300 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-200 disabled:!border-slate-300 disabled:!text-slate-600";
const btnSecondary =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-sky-400 !bg-sky-100 px-3 py-2 text-sm font-semibold !text-slate-800 transition hover:!bg-sky-50 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-100 disabled:!border-slate-300 disabled:!text-slate-500";
const btnDanger =
  "manage-btn inline-flex items-center justify-center rounded-md border !border-rose-400 !bg-rose-100 px-3 py-2 text-sm font-semibold !text-rose-800 transition hover:!bg-rose-50 disabled:cursor-not-allowed disabled:!opacity-100 disabled:!bg-slate-100 disabled:!border-slate-300 disabled:!text-slate-500";

function SectionCard({ title, subtitle, children }) {
  return (
    <section className="rounded-[12px] border border-slate-200 bg-white shadow-sm">
      <header className="border-b border-slate-200 px-5 py-4">
        <h2 className="text-base font-semibold text-slate-900">{title}</h2>
        {subtitle ? <p className="mt-1 text-sm text-slate-500">{subtitle}</p> : null}
      </header>
      <div className="px-5 py-4">{children}</div>
    </section>
  );
}

export default function ManageLevelsPage({ token, currentUser, onLogout, currentRoute, onNavigate }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [levels, setLevels] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [openLevelMenuId, setOpenLevelMenuId] = useState("");
  const [busyLevelId, setBusyLevelId] = useState("");
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [creatorFilter, setCreatorFilter] = useState("");
  const [blockedFilter, setBlockedFilter] = useState("all");
  const [reasonModalLevel, setReasonModalLevel] = useState(null);

  const navItems = useMemo(
    () => [
      { key: "users", label: "Users", icon: Users, route: ROUTE_MANAGE_USERS },
      { key: "levels", label: "Levels", icon: FolderKanban, route: ROUTE_MANAGE_LEVELS }
    ],
    []
  );

  const blockedFilterValue = useMemo(() => {
    if (blockedFilter === "blocked") return true;
    if (blockedFilter === "active") return false;
    return undefined;
  }, [blockedFilter]);

  const sortedUsers = useMemo(
    () =>
      [...users].sort((left, right) => {
        const leftName = (left.display_name || left.username || "").toLowerCase();
        const rightName = (right.display_name || right.username || "").toLowerCase();
        return leftName.localeCompare(rightName);
      }),
    [users]
  );

  const loadUsers = useCallback(async () => {
    try {
      const payload = await listWebBuilderUsers(token);
      setUsers(payload || []);
    } catch (userError) {
      setError(userError.message);
    }
  }, [token]);

  const loadLevels = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await listAdminWebBuilderLevels(token, {
        limit: 200,
        offset: 0,
        search: appliedSearch || undefined,
        creator_user_id: creatorFilter || undefined,
        is_blocked: blockedFilterValue
      });
      setLevels(payload || []);
    } catch (levelError) {
      setError(levelError.message);
    } finally {
      setLoading(false);
    }
  }, [appliedSearch, blockedFilterValue, creatorFilter, token]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadLevels();
  }, [loadLevels]);

  useEffect(() => {
    if (!openLevelMenuId) return;

    function handleDocumentClick(event) {
      const target = event.target;
      if (!(target instanceof Element)) {
        setOpenLevelMenuId("");
        return;
      }
      if (target.closest("[data-level-row-menu='true']")) {
        return;
      }
      setOpenLevelMenuId("");
    }

    function handleEscape(event) {
      if (event.key === "Escape") {
        setOpenLevelMenuId("");
      }
    }

    document.addEventListener("mousedown", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("mousedown", handleDocumentClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [openLevelMenuId]);

  function applyFilters(event) {
    event.preventDefault();
    setAppliedSearch(searchInput.trim());
  }

  function clearFilters() {
    setSearchInput("");
    setAppliedSearch("");
    setCreatorFilter("");
    setBlockedFilter("all");
  }

  async function toggleBlocked(level) {
    setOpenLevelMenuId("");
    setBusyLevelId(level.id);
    setError("");
    try {
      if (level.is_blocked) {
        await moderateAdminWebBuilderLevel(token, level.id, { is_blocked: false });
      } else {
        const rawReason = window.prompt("Reason for blocking this level (optional):", level.blocked_reason || "");
        if (rawReason === null) {
          setBusyLevelId("");
          return;
        }
        await moderateAdminWebBuilderLevel(token, level.id, {
          is_blocked: true,
          blocked_reason: rawReason.trim() || null
        });
      }
      await loadLevels();
    } catch (moderationError) {
      setError(moderationError.message);
    } finally {
      setBusyLevelId("");
    }
  }

  async function removeLevel(level) {
    setOpenLevelMenuId("");
    const confirmed = window.confirm(`Delete level ${level.level_code}?`);
    if (!confirmed) return;

    setBusyLevelId(level.id);
    setError("");
    try {
      await deleteAdminWebBuilderLevel(token, level.id);
      await loadLevels();
    } catch (deleteError) {
      setError(deleteError.message);
    } finally {
      setBusyLevelId("");
    }
  }

  function openReasonModal(level) {
    setOpenLevelMenuId("");
    setReasonModalLevel(level);
  }

  function closeReasonModal() {
    setReasonModalLevel(null);
  }

  async function exportLevelAsset(level) {
    setOpenLevelMenuId("");
    setBusyLevelId(level.id);
    setError("");

    try {
      const details = await getAdminWebBuilderLevel(token, level.id);
      const sourceLevel = details?.level_json;
      if (!sourceLevel || typeof sourceLevel !== "object") {
        throw new Error("Level JSON is missing for this record.");
      }

      const exportLevel = { ...sourceLevel };
      if (!exportLevel.id) {
        exportLevel.id = details.technical_id || details.level_code || "level_export";
      }

      const assetText = buildUnityAssetText(exportLevel);
      const safeFileName = `${details.level_code || "level_export"}`
        .replace(/[^A-Za-z0-9._-]+/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_+|_+$/g, "");

      const blob = new Blob([assetText], { type: "text/yaml;charset=utf-8" });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${safeFileName || "level_export"}.asset`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
    } catch (exportError) {
      setError(exportError.message || "Could not export level as .asset.");
    } finally {
      setBusyLevelId("");
    }
  }

  return (
    <main className="manage-tailwind manage-layout-shell bg-slate-100 font-sans text-slate-900">
      <div className="flex h-full min-h-0 bg-slate-100">
        <aside
          className={`manage-sidebar relative hidden border-r border-slate-800 bg-slate-900 text-slate-100 transition-all duration-200 md:flex md:flex-col ${
            sidebarCollapsed ? "md:w-20" : "md:w-64"
          }`}
        >
          <div className="flex items-center justify-between border-b border-slate-800 px-3 py-4">
            <div className={`overflow-hidden transition-all ${sidebarCollapsed ? "w-0" : "w-full"}`}>
              <p className="truncate text-sm font-semibold text-white">Builder Manager</p>
              <p className="truncate text-xs text-slate-400">{currentUser.username}</p>
            </div>
            <button
              type="button"
              className="manage-btn manage-sidebar-btn inline-flex h-8 w-8 items-center justify-center rounded-md border transition"
              onClick={() => setSidebarCollapsed((previous) => !previous)}
              aria-label="Toggle sidebar"
            >
              <span className="manage-hamburger-glyph" aria-hidden="true">
                ☰
              </span>
            </button>
          </div>

          <nav className="manage-sidebar-nav flex-1 space-y-1 p-3">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentRoute === item.route;
              return (
                <button
                  key={item.key}
                  type="button"
                  className={`manage-btn manage-sidebar-btn flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-semibold transition ${
                    isActive ? "is-active" : ""
                  }`}
                  onClick={() => onNavigate?.(item.route)}
                >
                  <Icon size={16} className="shrink-0" />
                  {!sidebarCollapsed ? <span>{item.label}</span> : null}
                </button>
              );
            })}
          </nav>

          <div className="shrink-0 border-t border-slate-800 p-3">
            <button type="button" className={`${btnSecondary} w-full`} onClick={() => onLogout?.()}>
              <LogOut size={16} className="mr-2" />
              {!sidebarCollapsed ? "Logout" : ""}
            </button>
          </div>
        </aside>

        <section className="manage-content-scroll flex-1 bg-slate-100 p-4 md:p-6">
          <div className="mx-auto flex w-full max-w-7xl flex-col gap-4">
            <header className="rounded-[12px] border border-slate-200 bg-white px-5 py-4 shadow-sm">
              <h1 className="text-lg font-semibold text-slate-900">Manage Builder Levels</h1>
              <p className="text-sm text-slate-500">Admin tools to review, block and delete created levels.</p>
            </header>

            <SectionCard title="Levels" subtitle="Filter by user, status, or text search.">
              {error ? (
                <p className="mb-3 rounded-md bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>
              ) : null}

              <form className="mb-4 grid gap-3 md:grid-cols-4" onSubmit={applyFilters}>
                <label className="text-sm text-slate-700 md:col-span-2">
                  <span className="mb-1 block">Search</span>
                  <input
                    className={inputClass}
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    placeholder="Code, display name, category, creator..."
                  />
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Creator</span>
                  <select
                    className={inputClass}
                    value={creatorFilter}
                    onChange={(event) => setCreatorFilter(event.target.value)}
                  >
                    <option value="">All users</option>
                    {sortedUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {(user.display_name || user.username).trim()} ({user.username})
                      </option>
                    ))}
                  </select>
                </label>

                <label className="text-sm text-slate-700">
                  <span className="mb-1 block">Status</span>
                  <select
                    className={inputClass}
                    value={blockedFilter}
                    onChange={(event) => setBlockedFilter(event.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="blocked">Blocked</option>
                  </select>
                </label>

                <div className="flex items-end gap-2 md:col-span-4">
                  <button type="submit" className={btnPrimary}>
                    <Search size={15} className="mr-1.5" />
                    Apply
                  </button>
                  <button type="button" className={btnSecondary} onClick={clearFilters}>
                    Clear
                  </button>
                </div>
              </form>

              {loading ? (
                <p className="text-sm text-slate-500">Loading levels...</p>
              ) : (
                <div className="overflow-x-auto md:overflow-visible rounded-lg border border-slate-200">
                  <table className="min-w-full text-sm">
                    <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                      <tr>
                        <th className="px-4 py-3">Code</th>
                        <th className="px-4 py-3">Display</th>
                        <th className="px-4 py-3">Creator</th>
                        <th className="px-4 py-3">Category</th>
                        <th className="px-4 py-3">Status</th>
                        <th className="px-4 py-3">Updated</th>
                        <th className="px-4 py-3 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {levels.map((level) => {
                        const busy = busyLevelId === level.id;
                        return (
                          <tr key={level.id} className="border-t border-slate-200 transition-colors hover:bg-slate-50/80">
                            <td className="px-4 py-3 font-medium text-slate-900">{level.level_code}</td>
                            <td className="px-4 py-3 text-slate-700">{level.display_name}</td>
                            <td className="px-4 py-3 text-slate-700">{level.creator_name || level.creator_username}</td>
                            <td className="px-4 py-3 text-slate-600">{level.category_name || "-"}</td>
                            <td className="px-4 py-3">
                              <span
                                className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                  level.is_blocked ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
                                }`}
                              >
                                {level.is_blocked ? "Blocked" : "Active"}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-slate-600">
                              {new Date(level.updated_at).toLocaleString()}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <div className="relative inline-flex" data-level-row-menu="true">
                                <button
                                  type="button"
                                  className="manage-btn manage-action-btn inline-flex h-9 w-9 items-center justify-center rounded-md border border-slate-300 bg-white !text-slate-800 transition hover:bg-slate-100"
                                  onClick={() => setOpenLevelMenuId((previous) => (previous === level.id ? "" : level.id))}
                                  disabled={busy}
                                  aria-label={`Open actions for ${level.level_code}`}
                                >
                                  <MoreVertical size={18} strokeWidth={2.25} />
                                </button>
                                {openLevelMenuId === level.id ? (
                                  <div className="absolute right-0 top-10 z-20 w-48 rounded-md border border-slate-200 bg-white p-1 shadow-lg">
                                    {level.is_blocked ? (
                                      <button
                                        type="button"
                                        className="manage-btn flex w-full items-center rounded px-2 py-2 text-left text-sm !text-slate-700 transition hover:bg-slate-100"
                                        onClick={() => openReasonModal(level)}
                                      >
                                        View block reason
                                      </button>
                                    ) : null}
                                    <button
                                      type="button"
                                      className="manage-btn flex w-full items-center rounded px-2 py-2 text-left text-sm !text-slate-700 transition hover:bg-slate-100"
                                      onClick={() => toggleBlocked(level)}
                                    >
                                      {level.is_blocked ? "Unblock level" : "Block level"}
                                    </button>
                                    <button
                                      type="button"
                                      className="manage-btn flex w-full items-center rounded px-2 py-2 text-left text-sm !text-slate-700 transition hover:bg-slate-100"
                                      onClick={() => exportLevelAsset(level)}
                                    >
                                      Export .asset
                                    </button>
                                    <button
                                      type="button"
                                      className="manage-btn flex w-full items-center rounded px-2 py-2 text-left text-sm !text-rose-700 transition hover:bg-rose-50"
                                      onClick={() => removeLevel(level)}
                                    >
                                      Delete level
                                    </button>
                                  </div>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </SectionCard>
          </div>
        </section>
      </div>

      {reasonModalLevel ? (
        <div
          className="fixed inset-0 z-[120] flex items-center justify-center bg-slate-900/55 p-4 backdrop-blur-[2px]"
          onClick={closeReasonModal}
        >
          <section
            className="w-full max-w-lg rounded-[12px] border border-slate-200 bg-white shadow-2xl"
            role="dialog"
            aria-modal="true"
            aria-label="Block reason"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="flex items-start justify-between gap-3 border-b border-slate-200 px-5 py-4">
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Block Reason</h3>
                <p className="mt-1 text-sm text-slate-500">{reasonModalLevel.level_code}</p>
              </div>
              <button type="button" className={btnSecondary} onClick={closeReasonModal}>
                Close
              </button>
            </header>
            <div className="space-y-3 px-5 py-4">
              <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {reasonModalLevel.blocked_reason || "No reason was provided."}
              </div>
              <p className="text-xs text-slate-500">
                Blocked at:{" "}
                {reasonModalLevel.blocked_at
                  ? new Date(reasonModalLevel.blocked_at).toLocaleString()
                  : "Unknown"}
              </p>
            </div>
          </section>
        </div>
      ) : null}
    </main>
  );
}
